import React, { useMemo, useEffect, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { fetchPyramidBoard } from "../app/resultSlice";

import PyramidMenu from "../components/Pyramid/PyramidMenu";
import WaitingModal from "../components/WaitingModal";
import Loading from "../components/Loading";
import { useResult } from "../hooks/useResult";
import usePyramid from "../hooks/usePyramid";

const UsersPyramid = lazy(() => import("../components/Pyramid/UsersPyramid"));

const Pyramid = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    results: players,
    isLoading,
    imgSize,
    isOnlineShow,
    selectedPlayer,
    isChallengeLoading,
    onSliderChange,
    setIsOnlineShow,
    setSelectedPlayer,
    sendQuickFight,
    sendScheduledFight,
    handleWaitingCancel,
  } = useResult({ forPyramid: true });

  const fetchFailed = !isLoading && (!players || players.length === 0);

  const { ghostPlayers } = usePyramid(!fetchFailed);

  /** Saison-aktive + eingestufte Spieler; aktive Accounts werden serverseitig auf Level 1 normalisiert. */
  const playerList = Array.isArray(players) ? players : [];

  const pyramidPlayers = useMemo(
    () =>
      playerList
        .filter((p) => Boolean(p.active) || Number(p.level) > 0)
        .map((p) => ({
          ...p,
          pyramidDisplayLevel: Number(p.level) || 0,
        })),
    [playerList]
  );

  const { allUsers } = useSelector((state) => state.auth);
  const connectedUsers = useMemo(
    () =>
      allUsers.filter(
        (val) => val.status === "online" || val.status === "occupied"
      ),
    [allUsers]
  );

  const pyramidUserKeys = useMemo(() => {
    const keys = new Set();
    pyramidPlayers.forEach((p) => {
      if (p.username) keys.add(p.username.toLowerCase());
    });
    return keys;
  }, [pyramidPlayers]);

  /** Online-Liste / Filter nur für Spieler, die auch auf der Pyramide stehen */
  const connectedOnPyramid = useMemo(
    () =>
      connectedUsers.filter((u) =>
        pyramidUserKeys.has(u.username?.toLowerCase())
      ),
    [connectedUsers, pyramidUserKeys]
  );

  useEffect(() => {
    if (!selectedPlayer?._id) return;
    const el = document.getElementById(String(selectedPlayer._id));
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedPlayer]);

  return (
    <div className="py-16 px-4">
      <div className="flex flex-col lg:flex-row gap-4 ">
        {/* mobile */}
        <PyramidMenu
          className="flex w-full p-2 flex-col lg:hidden bg-white dark:bg-gray-900 rounded-md shadow"
          players={pyramidPlayers}
          isLoading={isLoading}
          isOnlineShow={isOnlineShow}
          connectedUsers={connectedOnPyramid}
          setIsOnlineShow={setIsOnlineShow}
          setSelectedPlayer={setSelectedPlayer}
        />
        <div className="flex flex-col w-full py-4 px-2 lg:w-8/12 bg-white dark:bg-gray-900 rounded-md shadow">
          <div className="flex items-center space-x-4 h-[50px]">
            <p className="text-gray-900 dark:text-white">{t("zoomOut")}</p>
            <input
              id="medium-range"
              type="range"
              min={6}
              max={16}
              step={2}
              value={imgSize}
              className="w-full h-2 bg-gray-200 text-green-600 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              onChange={onSliderChange}
            />
            <p className="text-gray-900 dark:text-white">{t("zoomIn")}</p>
          </div>
          <p className="text-xs opacity-70 pb-2">
            {t("pyramidInactivityHint")}{" "}
            <Link className="underline font-semibold" to="/infos/additive">
              {t("additive")}
            </Link>
            .
          </p>
          <div className="overflow-y-auto h-[750px] custom-scrollbar py-4 max-h-70-vh divide-y divide-green-300 dark:divide-gray-400">
            {isLoading ? (
              <div className="flex flex-col gap-4">
                <Loading />
                <Loading />
                <Loading />
                <Loading />
              </div>
            ) : fetchFailed ? (
              <div className="text-center py-8 px-4 space-y-4">
                <p className="text-amber-600 dark:text-amber-400">
                  {t("pyramidLoadFailed", {
                    defaultValue:
                      "Spielerdaten konnten nicht geladen werden. Das Fight-Club-Backend antwortet nicht (502).",
                  })}
                </p>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
                  onClick={() => dispatch(fetchPyramidBoard())}
                >
                  {t("retry", { defaultValue: "Erneut laden" })}
                </button>
              </div>
            ) : (
              <Suspense fallback={<Loading />}>
                <UsersPyramid
                  onlineShow={isOnlineShow}
                  players={pyramidPlayers}
                  allPlayers={playerList}
                  ghostPlayers={ghostPlayers}
                  selectedPlayer={selectedPlayer}
                  connectedUsers={connectedOnPyramid}
                  imgSize={imgSize}
                  sendQuickFight={sendQuickFight}
                  sendScheduledFight={sendScheduledFight}
                />
              </Suspense>
            )}
          </div>
        </div>
        {/* desktop */}
        <PyramidMenu
          className="hidden bg-white dark:bg-gray-900 rounded-md shadow h-[832px] overflow-hidden overflow-y-auto custom-scrollbar px-2 py-4 lg:flex lg:flex-col md:px-6 w-6/12 lg:w-4/12"
          players={pyramidPlayers}
          isLoading={isLoading}
          isOnlineShow={isOnlineShow}
          connectedUsers={connectedOnPyramid}
          setIsOnlineShow={setIsOnlineShow}
          setSelectedPlayer={setSelectedPlayer}
        />
        <WaitingModal
          isShow={isChallengeLoading}
          text={t("waitingForAcceptance")}
          handleCancel={handleWaitingCancel}
        />
      </div>
    </div>
  );
};

export default Pyramid;
